import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc,
  getDoc,
  getDocs,
  updateDoc, 
  deleteDoc, 
  writeBatch,
  Timestamp,
  query,
  orderBy
} from 'firebase/firestore';
import { APP_ID, DEFAULTS } from '../config/constants';
import { initializeDefaultYears } from './yearsService';

// Get Firestore instance
const getDb = () => {
  return getFirestore();
};

// Helper: Get settings path
const getSettingsPath = (userId) => {
  return `artifacts/${APP_ID}/users/${userId}/settings`;
};

// Helper: Get user path
const getUserPath = (userId) => {
  return `artifacts/${APP_ID}/users/${userId}`;
};

// Default data
const DEFAULT_GENERAL_SETTINGS = {
  currentYear: DEFAULTS.YEAR,
  defaultCurrency: DEFAULTS.CURRENCY,
  language: DEFAULTS.LANGUAGE,
  createdAt: Timestamp.now(),
  updatedAt: Timestamp.now()
};

export const DEFAULT_INCOME_CATEGORIES = [
  { id: 'salary', name: 'Lương', icon: 'Briefcase', color: 'indigo', order: 1, isDefault: true },
  { id: 'bonus', name: 'Thưởng', icon: 'Gift', color: 'emerald', order: 2, isDefault: true },
  { id: 'investment', name: 'Đầu tư', icon: 'TrendingUp', color: 'blue', order: 3, isDefault: true },
  { id: 'business', name: 'Kinh doanh', icon: 'Store', color: 'purple', order: 4, isDefault: true },
  { id: 'freelance', name: 'Freelance', icon: 'Laptop', color: 'cyan', order: 5, isDefault: true },
  { id: 'other-income', name: 'Khác', icon: 'DollarSign', color: 'slate', order: 6, isDefault: true }
];

// budgetClass: 'need' (thiết yếu) | 'want' (mong muốn) — G3-B, bật quy tắc 50/30/20.
// Gán sẵn hợp lý, người dùng sửa được trong Cài đặt (ước lệ có chủ đích — phản biện P3)
export const DEFAULT_EXPENSE_CATEGORIES = [
  { id: 'food', name: 'Ăn uống', icon: 'Coffee', color: 'amber', order: 1, isDefault: true, budgetClass: 'need' },
  { id: 'transport', name: 'Di chuyển', icon: 'Car', color: 'blue', order: 2, isDefault: true, budgetClass: 'need' },
  { id: 'shopping', name: 'Mua sắm', icon: 'ShoppingCart', color: 'pink', order: 3, isDefault: true, budgetClass: 'want' },
  { id: 'entertainment', name: 'Giải trí', icon: 'Music', color: 'purple', order: 4, isDefault: true, budgetClass: 'want' },
  { id: 'bills', name: 'Hóa đơn', icon: 'FileText', color: 'red', order: 5, isDefault: true, budgetClass: 'need' },
  { id: 'health', name: 'Y tế', icon: 'Heart', color: 'rose', order: 6, isDefault: true, budgetClass: 'need' },
  { id: 'education', name: 'Giáo dục', icon: 'BookOpen', color: 'indigo', order: 7, isDefault: true, budgetClass: 'need' },
  { id: 'home', name: 'Nhà cửa', icon: 'Home', color: 'emerald', order: 8, isDefault: true, budgetClass: 'need' },
  { id: 'other-expense', name: 'Khác', icon: 'Wallet', color: 'slate', order: 9, isDefault: true, budgetClass: 'want' },
  // Danh mục hệ thống: nơi các khoản lãi vay/phí chuyển tự sinh được ghi nhận
  // (trước đây vắng mặt → bảng báo cáo theo danh mục không cộng ra tổng)
  { id: 'finance-cost', name: 'Chi phí tài chính', icon: 'Percent', color: 'orange', order: 10, isDefault: true, budgetClass: 'need' }
];

const DEFAULT_INCOME_SOURCES = [
  { id: 'company-main', name: 'Công ty chính', type: 'company', icon: 'Briefcase', color: 'indigo', order: 1, isDefault: true },
  { id: 'freelance-client', name: 'Khách hàng Freelance', type: 'client', icon: 'Users', color: 'emerald', order: 2, isDefault: true },
  { id: 'investment', name: 'Đầu tư', type: 'investment', icon: 'TrendingUp', color: 'blue', order: 3, isDefault: true },
  { id: 'business', name: 'Kinh doanh cá nhân', type: 'business', icon: 'Store', color: 'purple', order: 4, isDefault: true },
  { id: 'other-source', name: 'Nguồn khác', type: 'other', icon: 'HelpCircle', color: 'slate', order: 5, isDefault: false }
];

/**
 * Initialize default settings for new user
 * @param {string} userId 
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const initializeDefaultSettings = async (userId) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const batch = writeBatch(getDb());
    const settingsPath = getSettingsPath(userId);

    // 1. Set general settings
    const generalRef = doc(getDb(), settingsPath, 'general');
    batch.set(generalRef, DEFAULT_GENERAL_SETTINGS);

    // 2. Set income categories
    DEFAULT_INCOME_CATEGORIES.forEach(category => {
      const categoryRef = doc(getDb(), `${getUserPath(userId)}/incomeCategories`, category.id);
      batch.set(categoryRef, {
        ...category,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    });

    // 3. Set expense categories
    DEFAULT_EXPENSE_CATEGORIES.forEach(category => {
      const categoryRef = doc(getDb(), `${getUserPath(userId)}/expenseCategories`, category.id);
      batch.set(categoryRef, {
        ...category,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    });

    // 4. Set income sources
    DEFAULT_INCOME_SOURCES.forEach(source => {
      const sourceRef = doc(getDb(), `${getUserPath(userId)}/incomeSources`, source.id);
      batch.set(sourceRef, {
        ...source,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    });

    await batch.commit();

    // 5. Initialize default years (after batch commit)
    await initializeDefaultYears(userId);

    return { success: true };
  } catch (error) {
    console.error('Error initializing default settings:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get general settings
 * @param {string} userId 
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getGeneralSettings = async (userId) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const generalRef = doc(getDb(), getSettingsPath(userId), 'general');
    const generalSnap = await getDoc(generalRef);

    if (!generalSnap.exists()) {
      // Auto-initialize if not exists
      await initializeDefaultSettings(userId);
      return { success: true, data: DEFAULT_GENERAL_SETTINGS };
    }

    return { success: true, data: generalSnap.data() };
  } catch (error) {
    console.error('Error getting general settings:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update general settings
 * @param {string} userId 
 * @param {Object} updates 
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateGeneralSettings = async (userId, updates) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const generalRef = doc(getDb(), getSettingsPath(userId), 'general');
    
    await updateDoc(generalRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating general settings:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get income categories
 * @param {string} userId 
 * @returns {Promise<{success: boolean, data?: array, error?: string}>}
 */
export const getIncomeCategories = async (userId) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const categoriesRef = collection(getDb(), `${getUserPath(userId)}/incomeCategories`);
    const q = query(categoriesRef, orderBy('order', 'asc'));
    const snapshot = await getDocs(q);

    const categories = [];
    snapshot.forEach(doc => {
      categories.push({ id: doc.id, ...doc.data() });
    });

    // If empty, initialize defaults
    if (categories.length === 0) {
      await initializeDefaultSettings(userId);
      return { success: true, data: DEFAULT_INCOME_CATEGORIES };
    }

    return { success: true, data: categories };
  } catch (error) {
    console.error('Error getting income categories:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add income category
 * @param {string} userId 
 * @param {Object} category - {name, icon, color}
 * @returns {Promise<{success: boolean, id?: string, error?: string}>}
 */
export const addIncomeCategory = async (userId, category) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!category.name) throw new Error('Category name is required');

    // Get current categories to determine order
    const result = await getIncomeCategories(userId);
    const currentCategories = result.data || [];
    const maxOrder = currentCategories.reduce((max, cat) => Math.max(max, cat.order || 0), 0);

    const categoryId = `custom-${Date.now()}`;
    const categoryRef = doc(getDb(), `${getUserPath(userId)}/incomeCategories`, categoryId);

    const newCategory = {
      name: category.name.trim(),
      icon: category.icon || 'DollarSign',
      color: category.color || 'slate',
      order: maxOrder + 1,
      isDefault: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    await setDoc(categoryRef, newCategory);

    return { success: true, id: categoryId };
  } catch (error) {
    console.error('Error adding income category:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update income category
 * @param {string} userId 
 * @param {string} categoryId 
 * @param {Object} updates 
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateIncomeCategory = async (userId, categoryId, updates) => {
  try {
    if (!userId || !categoryId) throw new Error('User ID and Category ID are required');

    const categoryRef = doc(getDb(), `${getUserPath(userId)}/incomeCategories`, categoryId);
    
    await updateDoc(categoryRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating income category:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete income category (only custom, not default)
 * @param {string} userId 
 * @param {string} categoryId 
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteIncomeCategory = async (userId, categoryId) => {
  try {
    if (!userId || !categoryId) throw new Error('User ID and Category ID are required');

    // Check if it's a default category
    const categoryRef = doc(getDb(), `${getUserPath(userId)}/incomeCategories`, categoryId);
    const categorySnap = await getDoc(categoryRef);

    if (categorySnap.exists() && categorySnap.data().isDefault) {
      throw new Error('Cannot delete default category');
    }

    await deleteDoc(categoryRef);

    return { success: true };
  } catch (error) {
    console.error('Error deleting income category:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Reorder income categories
 * @param {string} userId 
 * @param {Array<string>} orderedIds - Array of category IDs in new order
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const reorderIncomeCategories = async (userId, orderedIds) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const batch = writeBatch(getDb());

    orderedIds.forEach((categoryId, index) => {
      const categoryRef = doc(getDb(), `${getUserPath(userId)}/incomeCategories`, categoryId);
      batch.update(categoryRef, {
        order: index + 1,
        updatedAt: Timestamp.now()
      });
    });

    await batch.commit();

    return { success: true };
  } catch (error) {
    console.error('Error reordering income categories:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get expense categories
 * @param {string} userId 
 * @returns {Promise<{success: boolean, data?: array, error?: string}>}
 */
export const getExpenseCategories = async (userId) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const categoriesRef = collection(getDb(), `${getUserPath(userId)}/expenseCategories`);
    const q = query(categoriesRef, orderBy('order', 'asc'));
    const snapshot = await getDocs(q);

    const categories = [];
    snapshot.forEach(doc => {
      categories.push({ id: doc.id, ...doc.data() });
    });

    // If empty, initialize defaults
    if (categories.length === 0) {
      await initializeDefaultSettings(userId);
      return { success: true, data: DEFAULT_EXPENSE_CATEGORIES };
    }

    return { success: true, data: categories };
  } catch (error) {
    console.error('Error getting expense categories:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add expense category
 * @param {string} userId 
 * @param {Object} category - {name, icon, color}
 * @returns {Promise<{success: boolean, id?: string, error?: string}>}
 */
export const addExpenseCategory = async (userId, category) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!category.name) throw new Error('Category name is required');

    // Get current categories to determine order
    const result = await getExpenseCategories(userId);
    const currentCategories = result.data || [];
    const maxOrder = currentCategories.reduce((max, cat) => Math.max(max, cat.order || 0), 0);

    const categoryId = `custom-${Date.now()}`;
    const categoryRef = doc(getDb(), `${getUserPath(userId)}/expenseCategories`, categoryId);

    const newCategory = {
      name: category.name.trim(),
      icon: category.icon || 'Wallet',
      color: category.color || 'slate',
      order: maxOrder + 1,
      isDefault: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    await setDoc(categoryRef, newCategory);

    return { success: true, id: categoryId };
  } catch (error) {
    console.error('Error adding expense category:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update expense category
 * @param {string} userId 
 * @param {string} categoryId 
 * @param {Object} updates 
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateExpenseCategory = async (userId, categoryId, updates) => {
  try {
    if (!userId || !categoryId) throw new Error('User ID and Category ID are required');

    const categoryRef = doc(getDb(), `${getUserPath(userId)}/expenseCategories`, categoryId);
    
    await updateDoc(categoryRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating expense category:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete expense category (only custom, not default)
 * @param {string} userId 
 * @param {string} categoryId 
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteExpenseCategory = async (userId, categoryId) => {
  try {
    if (!userId || !categoryId) throw new Error('User ID and Category ID are required');

    // Check if it's a default category
    const categoryRef = doc(getDb(), `${getUserPath(userId)}/expenseCategories`, categoryId);
    const categorySnap = await getDoc(categoryRef);

    if (categorySnap.exists() && categorySnap.data().isDefault) {
      throw new Error('Cannot delete default category');
    }

    await deleteDoc(categoryRef);

    return { success: true };
  } catch (error) {
    console.error('Error deleting expense category:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Reorder expense categories
 * @param {string} userId 
 * @param {Array<string>} orderedIds - Array of category IDs in new order
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const reorderExpenseCategories = async (userId, orderedIds) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const batch = writeBatch(getDb());

    orderedIds.forEach((categoryId, index) => {
      const categoryRef = doc(getDb(), `${getUserPath(userId)}/expenseCategories`, categoryId);
      batch.update(categoryRef, {
        order: index + 1,
        updatedAt: Timestamp.now()
      });
    });

    await batch.commit();

    return { success: true };
  } catch (error) {
    console.error('Error reordering expense categories:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// INCOME SOURCES MANAGEMENT
// ============================================

/**
 * Get all income sources for a user
 * @param {string} userId 
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getIncomeSources = async (userId) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const sourcesRef = collection(getDb(), `${getUserPath(userId)}/incomeSources`);
    const q = query(sourcesRef, orderBy('order', 'asc'));
    const snapshot = await getDocs(q);

    const sources = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: sources };
  } catch (error) {
    console.error('Error getting income sources:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Add new income source
 * @param {string} userId 
 * @param {Object} sourceData - {name, type, icon, color}
 * @returns {Promise<{success: boolean, id?: string, error?: string}>}
 */
export const addIncomeSource = async (userId, sourceData) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const sourcesRef = collection(getDb(), `${getUserPath(userId)}/incomeSources`);
    
    // Get current max order
    const snapshot = await getDocs(sourcesRef);
    const maxOrder = snapshot.docs.reduce((max, doc) => {
      const order = doc.data().order || 0;
      return order > max ? order : max;
    }, 0);

    const docRef = doc(sourcesRef);
    await setDoc(docRef, {
      ...sourceData,
      order: maxOrder + 1,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding income source:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update existing income source
 * @param {string} userId 
 * @param {string} sourceId 
 * @param {Object} updates 
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateIncomeSource = async (userId, sourceId, updates) => {
  try {
    if (!userId || !sourceId) throw new Error('User ID and Source ID are required');

    const sourceRef = doc(getDb(), `${getUserPath(userId)}/incomeSources`, sourceId);

    await updateDoc(sourceRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating income source:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete income source (only custom, not default)
 * @param {string} userId 
 * @param {string} sourceId 
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteIncomeSource = async (userId, sourceId) => {
  try {
    if (!userId || !sourceId) throw new Error('User ID and Source ID are required');

    // Check if it's a default source
    const sourceRef = doc(getDb(), `${getUserPath(userId)}/incomeSources`, sourceId);
    const sourceSnap = await getDoc(sourceRef);

    if (sourceSnap.exists() && sourceSnap.data().isDefault) {
      throw new Error('Cannot delete default source');
    }

    await deleteDoc(sourceRef);

    return { success: true };
  } catch (error) {
    console.error('Error deleting income source:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Reorder income sources
 * @param {string} userId 
 * @param {Array<string>} orderedIds - Array of source IDs in new order
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const reorderIncomeSources = async (userId, orderedIds) => {
  try {
    if (!userId) throw new Error('User ID is required');

    const batch = writeBatch(getDb());

    orderedIds.forEach((sourceId, index) => {
      const sourceRef = doc(getDb(), `${getUserPath(userId)}/incomeSources`, sourceId);
      batch.update(sourceRef, {
        order: index + 1,
        updatedAt: Timestamp.now()
      });
    });

    await batch.commit();

    return { success: true };
  } catch (error) {
    console.error('Error reordering income sources:', error);
    return { success: false, error: error.message };
  }
};
